/**
 * The Requests view's URL vocabulary (TRA-1397): the same param names as
 * the Trajectories page, so a reader who learned one listing can read a
 * link to the other. `range`, `q`, `sort` and `page` go through the
 * shared listing hook; this file holds what is the request log's own: the
 * status class, the metadata dropdowns and which columns sort.
 *
 * Kept free of React so the decisions run in the node vitest setup.
 */

import type {
  ListTeamRequestsApiDashboardRequestsGetSort,
  RequestLogItem,
} from '@/api/generated/model'
import { clientMetadataFilters } from '@/components/listing/clientMetadata'
import {
  plainFilterValue,
  type MetadataFilterSpec,
  type MetadataParam,
  type SortState,
} from '@/components/listing/listingParams'
import type { TeamRequestRange } from '@/hooks/useUsage'

import {
  REQUEST_RANGE_OPTIONS,
  REQUEST_STATUS_OPTIONS,
  type RequestStatusFilter,
} from './requestLogOptions'

export type RequestSortField = ListTeamRequestsApiDashboardRequestsGetSort

export const REQUEST_RANGES: TeamRequestRange[] = REQUEST_RANGE_OPTIONS.map((o) => o.value)
// Last 7 days, like Trajectories: a word search over all time on a busy
// team is the slow query from TRA-1386. All time is one pick away.
export const DEFAULT_REQUEST_RANGE: TeamRequestRange = '7d'
export const DEFAULT_REQUEST_SORT: SortState<RequestSortField> = { field: 'last', desc: true }

/** The Model column exists only on the team-wide log, so only there can
    it sort or filter. On a model page `sort=model.asc` reads as the
    default and `model=` is ignored. */
export function requestSortFields(teamWide: boolean): RequestSortField[] {
  return teamWide ? ['last', 'model', 'run'] : ['last', 'run']
}

export function requestMetadataFilters(teamWide: boolean): MetadataFilterSpec<RequestLogItem>[] {
  const client = clientMetadataFilters<RequestLogItem>()
  if (!teamWide) return client
  return [
    { param: 'model', label: 'Model', values: (i) => plainFilterValue(i.request_model) },
    ...client,
  ]
}

export function requestMetadataParams(teamWide: boolean): MetadataParam[] {
  return requestMetadataFilters(teamWide).map((f) => f.param)
}

/** `status=3xx` in a shared link means all, not a 422. */
export function parseRequestStatus(param: string | null): RequestStatusFilter | 'all' {
  const option = REQUEST_STATUS_OPTIONS.find((o) => o.value === param)
  return option === undefined ? 'all' : option.value
}
